import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import type {
  StickyBannerProps,
  StickyBannerState,
  StickyBannerStore,
} from './sticky-banner.types';

const defaultState: StickyBannerState = {
  banners: {},
};

const useStickyBannerStateRaw = create<StickyBannerStore>()((set) => ({
  ...defaultState,
  actions: {
    open: (key: string, props: StickyBannerProps) =>
      set((prev) => {
        const banners = prev.banners;
        const isOtherBannerOpen = Object.keys(banners).some(
          (existingKey) => existingKey !== key && banners[existingKey]?.isOpen
        );
        const offset = isOtherBannerOpen ? 100 : 0;

        return {
          banners: {
            ...banners,
            [key]: { isOpen: true, isCollapsed: false, props, offset },
          },
        };
      }),
    close: (key: string) =>
      set((prev) => ({ banners: { ...prev.banners, [key]: undefined } })),
    toggleCollapse: (key: string) =>
      set((prev) => {
        const currentBanner = prev.banners[key];
        if (!currentBanner) return prev;

        return {
          banners: {
            ...prev.banners,
            [key]: { ...currentBanner, isCollapsed: !currentBanner.isCollapsed },
          },
        };
      }),
    updateProps: (key: string, props: Partial<StickyBannerProps>) => {
      set((prev) => {
        const currentBanner = prev.banners[key];
        if (!currentBanner) return prev;

        return {
          banners: {
            ...prev.banners,
            [key]: {
              ...currentBanner,
              props: {
                ...currentBanner.props,
                ...props,
              },
            },
          },
        };
      });
    },
  },
}));

export const useStickyBannerState = (key: string) =>
  useStickyBannerStateRaw(useShallow((state) => state.banners[key]));

export const useStickyBannerActions = () =>
  useStickyBannerStateRaw((state) => state.actions);
